import {
  Badge,
  Button,
  Column,
  Columns,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { NotificationHistoryType } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { AppLink } from 'components/appLink';

import { TopbarButton } from '../topbarButton';

import { NotificationHistoryItem } from './notificationHistoryItem';
import styles from './notificationTopbarButton.module.scss';

type PropsT = {
  alignCenter?: boolean;
  notificationsHistory: ReadonlyArray<NotificationHistoryType>;
  notificationHistoryReset: () => void;
};

const numberOfVisibleNotifications = 3;

export const NotificationTopbarButton = ({
  alignCenter,
  notificationsHistory,
  notificationHistoryReset,
}: PropsT): JSX.Element => {
  const onToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      ampli.topbarNotificationsOpened();
    }
  }, []);

  const numberOfUnseenNotifications = useMemo(
    () =>
      notificationsHistory.reduce(
        (total, { unseen }) => (unseen ? total + 1 : total),
        0,
      ),
    [notificationsHistory],
  );

  const dropdownNotifications = useMemo(
    () => notificationsHistory.filter(({ showInDropdown }) => showInDropdown),
    [notificationsHistory],
  );

  const visibleNotifications = useMemo(
    () =>
      dropdownNotifications
        .slice(-numberOfVisibleNotifications) // Last n elements
        .reverse(),
    [dropdownNotifications],
  );

  const notVisibleNotificationsNumber =
    dropdownNotifications.length - visibleNotifications.length;

  return (
    <DropdownButton
      icon="bell"
      className={styles.root}
      onToggle={onToggle}
      noArrow
      noStyle
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ closeDropdown }) => (
            <Columns>
              <Column
                className={classnames(
                  {
                    [styles.fullWidthContent]: alignCenter,
                  },
                  styles.content,
                )}
              >
                <Column.Row slottable borderBottom>
                  <Column.Slot placement="start">
                    <Text.Heading>Notifications</Text.Heading>
                  </Column.Slot>

                  <Column.Slot placement="end">
                    {visibleNotifications.length > 0 && (
                      <Button
                        variant="tertiary"
                        onClick={notificationHistoryReset}
                      >
                        Mark all as seen
                      </Button>
                    )}

                    <Badge size="small" variant="info">
                      {numberOfUnseenNotifications}
                    </Badge>
                  </Column.Slot>
                </Column.Row>

                <Column.Row
                  scrollable
                  noHorizontalPadding
                  noVerticalPadding={visibleNotifications.length > 0}
                >
                  {visibleNotifications.length ? (
                    visibleNotifications.map((notification) => (
                      <NotificationHistoryItem
                        key={notification.id}
                        notification={notification}
                        onActionHandled={closeDropdown}
                      />
                    ))
                  ) : (
                    <EmptyState
                      title="No notifications"
                      description="There are no unread notifications"
                      icon={null}
                    />
                  )}
                </Column.Row>

                <Column.Row slottable noMinHeight borderTop>
                  <Column.Slot placement="center">
                    <AppLink path="/notifications" onClick={closeDropdown}>
                      {notVisibleNotificationsNumber > 0
                        ? `See all notifications (${notVisibleNotificationsNumber} more new)`
                        : 'See all notifications'}
                    </AppLink>
                  </Column.Slot>
                </Column.Row>
              </Column>
            </Columns>
          )}
        </DropdownButtonContext.Consumer>
      }
      dropdownProps={{
        className: classnames({
          [styles.dropdownMenuAlignCenter]: alignCenter,
        }),
      }}
    >
      <TopbarButton
        showNotificationIndicator={numberOfUnseenNotifications > 0}
        icon="bell"
        tooltip="Notifications"
      />
    </DropdownButton>
  );
};
